import { Box, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Transitions } from 'c1ui/theme/C1Transitions'
import { C1Dot } from 'components/core/dot'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { useHasBuiltinRole, useRoleChecker, useUnsafeFeatureChecker } from 'components/no-ui/capability'
import { NavItemProps, useMutedDotCache } from 'components/pages/sidebar/CollapsingSidebar'
import { RoleID } from 'lib/constants/system'
import { unsafeRoute } from 'lib/routes'
import { useRouter } from 'next/router'
import { useRef, useEffect, useState, ReactElement } from 'react'

type HorizontalAppBarNavProps = {
    navItems: NavItemProps[]
}

const durationMs = 300

type transitionProps = {
    property?: string
    duration?: string
    timingFunction?: string
    delay?: string
}

type computedTransition = {
    transitionProperty: string
    transitionDuration: string
    transitionTimingFunction: string
    transitionDelay: string
}

const transitionHelper = (...transitions: transitionProps[]): computedTransition => ({
    transitionProperty: transitions.map((t) => t?.property).join(','),
    transitionDuration: transitions.map((t) => t?.duration).join(','),
    transitionTimingFunction: transitions.map((t) => t?.timingFunction).join(','),
    transitionDelay: transitions.map((t) => t?.delay).join(','),
})

export const HorizontalAppBarNav: React.FC<HorizontalAppBarNavProps> = ({ navItems }) => {
    const theme = useTheme()
    const isMdOrLess = useMediaQuery(theme.breakpoints.down('lg'))
    const router = useRouter()
    const [value, setValue] = useState<number | false>(false)
    const hasRole = useRoleChecker()
    const hasFeature = useUnsafeFeatureChecker()
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const [transitionDistance, setTransitionDistance] = useState(0)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const [hasChanged, setHasChanged] = useState(false)

    // Determine which tab should be selected based on the current route
    useEffect(() => {
        const selectedIndex = navItems.findIndex((item) => {
            const selectedPaths = [item.pathname, ...(item.selectedPaths || [])]
            return selectedPaths.some((path) => {
                const regex = RegExp(`^\\${path}.*$`, 'g')
                return (router.pathname.match(regex) !== null && path !== '/') || (path === '/' && router.pathname === '/')
            })
        })

        const newIndex = selectedIndex !== -1 ? selectedIndex : false

        setValue(newIndex)

        if (newIndex !== value) {
            if (value !== false) setHasChanged(true)
            setTransitionDistance(Math.abs(newIndex !== false ? newIndex - (value || 0) : 0))
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            timeoutRef.current = setTimeout(() => {
                setTransitionDistance(0)
            }, durationMs * 0.7)
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [router.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

    // Filter out items that shouldn't be rendered based on roles/features
    const filteredNavItems = navItems.filter((navItemProps) => {
        // nav items are normally gated by editor/owner, but we want readonly superadmins to see admin nav items
        if (isReadOnlySuperAdmin) return true
        // If any features are specified, the user must have any of them
        const meetsFF = navItemProps.features?.length ? navItemProps.features.some(hasFeature) : true
        const meetsRole = navItemProps.roles?.length ? navItemProps.roles.some(hasRole) : true
        const nohideOnFeatures = navItemProps.hideOnFeatures?.length ? !navItemProps.hideOnFeatures.some(hasFeature) : true
        return meetsFF && meetsRole && nohideOnFeatures
    })

    const transitions = !hasChanged ? {
        transition: 'none',
    } : transitionHelper(
        { property: 'width', duration: `${durationMs}ms`, timingFunction: C1Transitions.easing.linearSpringC },
        { property: 'left', duration: `${durationMs}ms`, timingFunction: C1Transitions.easing.linearSpringC },
        { property: 'transform', duration: `${durationMs * 0.3}ms`, timingFunction: C1Transitions.easing.linearSpringC },
    )

    return (
        <Box
            component="nav"
            sx={{ paddingLeft: { md: 0, lg: 5 } }}
            aria-label="Main navigation"
        >
            <Tabs
                value={value}
                variant="scrollable"
                scrollButtons={false}
                selectionFollowsFocus={false}
                onKeyDown={(e): void => {
                    // Disable arrow key navigation for navigation links
                    // Navigation should use Tab key, not arrow keys (WCAG guideline)
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault()
                        e.stopPropagation()
                    }
                }}
                sx={{
                    minHeight: 'auto',
                    border: 'none',
                    overflow: 'visible',

                    '& .MuiTabs-flexContainer': {
                        gap: isMdOrLess ? 1 : 2,
                        zIndex: 2,
                        position: 'relative',
                    },

                    '& .MuiTabs-scroller': {
                        overflow: 'visible',
                    },

                    '& .MuiTabs-indicator': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: C1BorderRadius.regular,
                        height: 'auto',
                        top: 0,
                        zIndex: 1,
                        transform: transitionDistance
                            ? `scaleY(${1 - (transitionDistance / 15)}) scaleX(${1 + (transitionDistance / 40)})`
                            : 'scaleY(1) scaleX(1)',
                        ...transitions,
                    },
                }}
            >
                {filteredNavItems.map((item, index) => (
                    <HorizontalAppBarNavTab
                        key={item.id}
                        navItem={item}
                        index={index}
                    />
                ))}
            </Tabs>
        </Box>
    )
}

type HorizontalAppBarNavTabProps = {
    navItem: NavItemProps
    index: number
}

const HorizontalAppBarNavTab: React.FC<HorizontalAppBarNavTabProps> = ({ navItem, index }) => {
    const [dotIsMuted, setDotIsMuted] = useMutedDotCache(navItem.id)
    const prevCount = useRef(navItem.count)

    useEffect(() => {
        if (navItem.count === undefined) return

        if (prevCount.current && prevCount.current < navItem.count) {
            setDotIsMuted(false)
        }

        prevCount.current = navItem.count
    }, [navItem.count, navItem.id, setDotIsMuted])

    const router = useRouter()
    const shouldShowDot = (navItem.count !== undefined && navItem.count > 0) && !dotIsMuted

    const selectedPaths = [navItem.pathname, ...(navItem.selectedPaths || [])]
    const isSelected = selectedPaths.some((path) => {
        const regex = RegExp(`^\\${path}.*$`, 'g')
        return (router.pathname.match(regex) !== null && path !== '/') || (path === '/' && router.pathname === '/')
    })

    const handleClick = (): void => {
        setDotIsMuted(true)
    }

    const TabLabel = (): ReactElement => (
        <Stack direction="row" alignItems="center" spacing={1}>
            {navItem.icon && <C1Icon icon={navItem.icon} sx={{ color: 'primary.contrastText', fontSize: 20 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                    component="span"
                    sx={{
                        position: 'relative',
                        color: isSelected ? 'primary.contrastText' : 'inherit',

                        // Creates an invisible duplicate of the text with bold font weight to prevent layout shift
                        // when the text becomes bold on hover/selection. The ::after pseudo-element reserves
                        // the space needed for the bold text, keeping the tab width consistent.
                        '&::after': {
                            content: 'attr(data-text)',
                            fontWeight: 600,
                            height: 0,
                            visibility: 'hidden',
                            overflow: 'hidden',
                            userSelect: 'none',
                            pointerEvents: 'none',
                            display: 'block',
                        },
                    }}
                    data-text={navItem.label}
                >
                    <Typography
                        component="span"
                        className="nav-label-text"
                        sx={{
                            fontWeight: isSelected ? 600 : 450,
                        }}
                    >
                        {navItem.label}
                    </Typography>
                </Box>
                {shouldShowDot && <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                    <C1Dot />
                </Box>}
            </Box>
        </Stack>
    )

    return <Tab
        component={C1Link}
        href={unsafeRoute(navItem.pathname)}
        value={index}
        disableRipple
        label={<TabLabel />}
        onClick={handleClick}
        aria-label={navItem.label}
        aria-current={isSelected ? 'page' : undefined}
        tabIndex={0}
        sx={{
            minHeight: 'unset',
            minWidth: 'unset',
            py: 1,
            px: 3,
            marginLeft: navItem.isNewSection ? 4 : 0,
            borderRadius: C1BorderRadius.regular,
            opacity: 1,
            transition: 'background-color 0.2s ease-in-out',
            backgroundColor: 'transparent',
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'none',

            '&:hover': {
                color: 'primary.contrastText',
                background: 'transparent',

                '& .nav-label-text': {
                    fontWeight: 600,
                },
            },

            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.contrastText',
                outlineOffset: '2px',
                color: 'primary.contrastText',
                background: 'transparent',

                '& .nav-label-text': {
                    fontWeight: 600,
                },
            },
        }}
    />
}
